import React, { useMemo, useState, useCallback } from 'react';
import { Table, TableColumnsType, Button, Space, Tag } from 'antd';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { GET_WAREHOUSES_FOR_HEADQUARTERS, WarehousesResponse } from '../../../graphql/queries/getWarehouseForHeadquarters';
import { SiteType } from '../../../graphql/types/SiteType';
import { HasResource } from '../../../components/HasResource';
import { ResourceCode } from '../../../constants/resourceCodes';
import AbAddressReviewFilterArea, {
  AbAddressReviewFilterOptions,
  DEFAULT_AB_ADDRESS_REVIEW_FILTER_OPTIONS,
} from './components/AbAddressReviewFilterArea';
import AbAddressReviewDrawer, { AbAddressReviewDrawerMode } from './components/AbAddressReviewDrawer';
import {
  GET_AB_ADDRESS_REVIEW_LISTING,
  GetAbAddressReviewListingResponse,
  AbAddressMappingType,
} from './graphql/abAddressReviewGraphql';

const PAGE_SIZE = 10;

const reviewStatusTag = (status: number, t: (k: string) => string) => {
  switch (status) {
    case 1:
      return <Tag color="green">{t('mapTool.abAddressReview.status.approved')}</Tag>;
    case 2:
      return <Tag color="red">{t('mapTool.abAddressReview.status.rejected')}</Tag>;
    case 0:
    default:
      return <Tag color="orange">{t('mapTool.abAddressReview.status.pending')}</Tag>;
  }
};

const AbAddressReviewPage: React.FC = () => {
  const { t } = useTranslation();

  const [filterOptions, setFilterOptions] = useState<AbAddressReviewFilterOptions>(
    DEFAULT_AB_ADDRESS_REVIEW_FILTER_OPTIONS
  );
  const [appliedFilters, setAppliedFilters] = useState<AbAddressReviewFilterOptions>(
    DEFAULT_AB_ADDRESS_REVIEW_FILTER_OPTIONS
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<AbAddressReviewDrawerMode>('view');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: warehouseData } = useQuery<WarehousesResponse>(GET_WAREHOUSES_FOR_HEADQUARTERS);
  // 所属 HUB 只展示 HUB，不展示 WHS 子仓
  const hubOptions = useMemo(
    () => (warehouseData?.warehouses || [])
      .filter((w) => w.siteType === SiteType.HUB)
      .map((w) => ({ label: w.name, value: Number(w.id) })),
    [warehouseData]
  );

  const { data, loading, refetch } = useQuery<GetAbAddressReviewListingResponse>(
    GET_AB_ADDRESS_REVIEW_LISTING,
    {
      variables: {
        hubId: appliedFilters.hubId ? Number(appliedFilters.hubId) : undefined,
        id: appliedFilters.id ? Number(appliedFilters.id) : undefined,
        aAddress: appliedFilters.aAddress || undefined,
        bAddress: appliedFilters.bAddress || undefined,
        reporterName: appliedFilters.reporterName || undefined,
        status: appliedFilters.status != null && appliedFilters.status !== ''
          ? Number(appliedFilters.status)
          : undefined,
        page: currentPage,
        size: PAGE_SIZE,
      },
      fetchPolicy: 'no-cache',
    }
  );

  const openDrawer = (id: string, mode: AbAddressReviewDrawerMode) => {
    setSelectedId(id);
    setDrawerMode(mode);
    setDrawerOpen(true);
  };

  const handleReviewed = useCallback(() => {
    const list = data?.getAbAddressReviewListing?.content ?? [];
    const currentIdx = list.findIndex((r: AbAddressMappingType) => r.id === selectedId);
    const next =
      currentIdx >= 0 ? list.slice(currentIdx + 1).find((r: AbAddressMappingType) => r.reviewStatus === 0) : undefined;
    if (next) {
      setSelectedId(next.id);
      setDrawerMode('review');
    } else {
      setDrawerOpen(false);
      setSelectedId(null);
      refetch();
    }
  }, [data, selectedId, refetch]);

  const handleFilter = () => {
    setAppliedFilters({ ...filterOptions });
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilterOptions(DEFAULT_AB_ADDRESS_REVIEW_FILTER_OPTIONS);
    setAppliedFilters(DEFAULT_AB_ADDRESS_REVIEW_FILTER_OPTIONS);
    setCurrentPage(1);
  };

  const columns: TableColumnsType<AbAddressMappingType> = [
    {
      title: t('mapTool.abAddressReview.table.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
    },
    {
      title: t('mapTool.abAddressReview.table.hub'),
      key: 'hub',
      width: 140,
      ellipsis: true,
      render: (_, record) => record.hubName ?? record.hubId ?? '—',
    },
    {
      title: t('mapTool.abAddressReview.table.aAddress'),
      dataIndex: 'aFullAddress',
      key: 'aFullAddress',
      ellipsis: true,
      render: (v: string | null | undefined) => v ?? '—',
    },
    {
      title: t('mapTool.abAddressReview.table.bAddress'),
      dataIndex: 'bFullAddress',
      key: 'bFullAddress',
      ellipsis: true,
      render: (v: string | null | undefined) => v ?? '—',
    },
    {
      title: t('mapTool.abAddressReview.table.reporter'),
      key: 'reporter',
      width: 120,
      align: 'center',
      render: (_, record) => record.reporterName ?? record.reporterAccountId ?? '—',
    },
    {
      title: t('mapTool.abAddressReview.table.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      align: 'center',
      render: (v: string | null | undefined) =>
        v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—',
    },
    {
      title: t('mapTool.abAddressReview.table.status'),
      dataIndex: 'reviewStatus',
      key: 'reviewStatus',
      width: 110,
      align: 'center',
      render: (v: number) => reviewStatusTag(v, t),
    },
    {
      title: t('components.button.action') as string,
      key: 'action',
      width: 140,
      fixed: 'right' as const,
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" size="small" onClick={() => openDrawer(record.id, 'view')}>
            {t('mapTool.abAddressReview.actions.view')}
          </Button>
          <HasResource resourceCodes={[ResourceCode.menu.AB_ADDRESS_REVIEW_SUBMIT]}>
            <Button
              type="link"
              size="small"
              disabled={record.reviewStatus !== 0}
              onClick={() => openDrawer(record.id, 'review')}
            >
              {t('mapTool.abAddressReview.actions.review')}
            </Button>
          </HasResource>
        </Space>
      ),
    },
  ];

  const pageData = data?.getAbAddressReviewListing;

  return (
    <div className="p-6">
      <AbAddressReviewFilterArea
        filters={filterOptions}
        setFilters={setFilterOptions}
        hubOptions={hubOptions}
      />

      <div className="flex justify-end items-center mb-4 mt-4 gap-2">
        <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
          <span className="font-bold">{t('components.button.filter')}</span>
        </Button>
        <Button onClick={handleResetFilters} className="w-auto min-w-24">
          <span className="font-bold">{t('components.button.reset')}</span>
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <Table
          columns={columns}
          dataSource={pageData?.content || []}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: PAGE_SIZE,
            total: pageData?.totalElements || 0,
            showSizeChanger: false,
            showTotal: (total) => t('mapTool.abAddressReview.table.paginationTotal', { total }),
            onChange: (page) => setCurrentPage(page),
          }}
        />
      </div>

      <AbAddressReviewDrawer
        open={drawerOpen}
        id={selectedId}
        mode={drawerMode}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedId(null);
          refetch();
        }}
        onReviewed={handleReviewed}
      />
    </div>
  );
};

export default AbAddressReviewPage;
